import { useState, useEffect } from 'react';
import { PlusCircle, Pencil, Trash2, Check, X } from 'lucide-react';
import { Pueblo } from '../../types';
import { getPueblos, createPueblo, updatePueblo, deletePueblo } from '../../lib/db';
import { ConfirmDialog } from '../ConfirmDialog';

export function PuebloManager() {
  const [pueblos, setPueblos] = useState<Pueblo[]>([]);
  const [loading, setLoading] = useState(true);
  const [addingName, setAddingName] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<Pueblo | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPueblos();
  }, []);

  async function loadPueblos() {
    try {
      const data = await getPueblos();
      setPueblos(data);
    } catch (err) {
      setError('Error al cargar los pueblos');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  const handleAdd = async () => {
    const name = addingName.trim();
    if (!name) return;
    try {
      const nuevo = await createPueblo(name);
      setPueblos(prev => [...prev, nuevo]);
      setAddingName('');
      setIsAdding(false);
      setError(null);
    } catch (err) {
      setError('Error al crear el pueblo');
      console.error(err);
    }
  };

  const handleEdit = async (id: string) => {
    const name = editingName.trim();
    if (!name) return;
    try {
      await updatePueblo(id, name);
      setPueblos(prev => prev.map(p => p.id === id ? { ...p, name } : p));
      setEditingId(null);
      setError(null);
    } catch (err) {
      setError('Error al actualizar el pueblo');
      console.error(err);
    }
  };

  const handleDelete = async (pueblo: Pueblo) => {
    try {
      await deletePueblo(pueblo.id);
      setPueblos(prev => prev.filter(p => p.id !== pueblo.id));
      setDeleteConfirm(null);
      setError(null);
    } catch (err) {
      setError('Error al eliminar el pueblo');
      console.error(err);
    }
  };

  if (loading) return <div className="text-sm text-gray-500">Cargando pueblos...</div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center pb-4 border-b">
        <h2 className="text-xl font-semibold text-gray-900">Pueblos del CRA</h2>
        {!isAdding && (
          <button
            onClick={() => { setIsAdding(true); setAddingName(''); }}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
          >
            <PlusCircle className="mr-2 w-4 h-4" />
            Añadir Pueblo
          </button>
        )}
      </div>

      {isAdding && (
        <div className="flex gap-2 items-center p-3 bg-gray-50 rounded-md">
          <input
            type="text"
            autoFocus
            value={addingName}
            onChange={e => setAddingName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleAdd(); if (e.key === 'Escape') { setIsAdding(false); setAddingName(''); } }}
            placeholder="Nombre del pueblo"
            className="flex-1 px-3 py-2 text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <button
            onClick={handleAdd}
            className="p-2 text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
            title="Guardar"
          >
            <Check className="w-4 h-4" />
          </button>
          <button
            onClick={() => { setIsAdding(false); setAddingName(''); }}
            className="p-2 text-gray-500 rounded-md hover:bg-gray-200"
            title="Cancelar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}

      {pueblos.length === 0 && !isAdding && (
        <p className="py-8 text-sm text-center text-gray-500">
          No hay pueblos configurados. Añade el primer pueblo del CRA.
        </p>
      )}

      <ul className="divide-y divide-gray-100 rounded-lg border">
        {pueblos.map(pueblo => (
          <li key={pueblo.id} className="flex justify-between items-center px-4 py-3">
            {editingId === pueblo.id ? (
              <div className="flex flex-1 gap-2 items-center">
                <input
                  type="text"
                  autoFocus
                  value={editingName}
                  onChange={e => setEditingName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleEdit(pueblo.id); if (e.key === 'Escape') setEditingId(null); }}
                  className="flex-1 px-3 py-1 text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <button onClick={() => handleEdit(pueblo.id)} className="p-1.5 text-white bg-indigo-600 rounded-md hover:bg-indigo-700" title="Guardar">
                  <Check className="w-4 h-4" />
                </button>
                <button onClick={() => setEditingId(null)} className="p-1.5 text-gray-500 rounded-md hover:bg-gray-200" title="Cancelar">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <span className="text-sm font-medium text-gray-800">{pueblo.name}</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setEditingId(pueblo.id); setEditingName(pueblo.name); }}
                    className="p-1.5 text-gray-400 rounded hover:text-indigo-600 hover:bg-indigo-50"
                    title="Editar"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(pueblo)}
                    className="p-1.5 text-gray-400 rounded hover:text-red-600 hover:bg-red-50"
                    title="Eliminar"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </>
            )}
          </li>
        ))}
      </ul>

      <ConfirmDialog
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => deleteConfirm && handleDelete(deleteConfirm)}
        title="Eliminar pueblo"
        message={`¿Eliminar "${deleteConfirm?.name}"? Los alumnos asignados a este pueblo perderán esa asignación.`}
      />
    </div>
  );
}
